import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import RedoIcon from '@mui/icons-material/Redo';
import SettingsIcon from '@mui/icons-material/Settings';
import { Box, CircularProgress, IconButton, Modal, Stack, Typography } from '@mui/material';
import { observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import { useI18n } from '../../../../../../application/language/useI18n';
import { ProjectInfoDomain } from '../store/ProjectInfoDomain';
import { getInvitationLink, lowercaseRoleFirst, removeHTTPProtocol } from './utils';

export interface IInvitationLinkProperties {
  domain: ProjectInfoDomain;
  projectId: string;
}

export const InvitationLinkSettings = observer(({ domain, projectId }: IInvitationLinkProperties) => {
  const { translate } = useI18n();
  const [open, setOpen] = useState(false);
  const [itemsToDelete, setItemsToDelete] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (open) {
      domain.fetchInvitationLinks(projectId).finally(() => setLoading(false));
    }
  }, [open]);

  const links = domain.ui.invitationLinks.list || [];

  const handleOpen = () => setOpen(true);
  const handleClose = async () => {
    await handleConfirmDelete();
    if (!deleting) {
      setOpen(false);
      setItemsToDelete([]);
    }
  };

  const toggleDelete = (id: string) => {
    setItemsToDelete((prev) => (prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]));
  };

  const handleConfirmDelete = async () => {
    setDeleting(true);

    const deletePromises = itemsToDelete.map((id) => domain.deleteInvitationLink({ id }));

    try {
      await Promise.all(deletePromises);
      domain.fetchInvitationLinks(projectId);
      setItemsToDelete([]);
      setOpen(false);
    } catch (error) {
      console.error('Ошибка при удалении ссылок:', error);
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    const callActionBeforeLeave = async () => {
      if (itemsToDelete.length > 0) {
        try {
          await Promise.all(itemsToDelete.map((id) => domain.deleteInvitationLink({ id })));
        } catch (error) {
          console.error('Ошибка при удалении ссылок:', error);
        }
      }
    };
    window.addEventListener('beforeunload', callActionBeforeLeave);

    return () => {
      window.removeEventListener('beforeunload', callActionBeforeLeave);
    };
  }, [itemsToDelete, domain]);

  return (
    <>
      <IconButton aria-label="invitation-links-settings" onClick={handleOpen}>
        <SettingsIcon />
      </IconButton>

      <Modal open={open} onClose={handleClose}>
        <Box
          sx={{
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '500px',
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 3,
            borderRadius: 2,
            position: 'relative',
          }}
        >
          <IconButton
            onClick={handleClose}
            sx={{ position: 'absolute', top: 8, right: 8, color: 'gray' }}
            disabled={deleting}
          >
            <CloseIcon />
          </IconButton>

          <Box display="flex" flexDirection="column" gap="24px">
            <Box display="flex" flexDirection="column" gap="8px">
              <Typography variant="h5" textAlign='center' >
                {translate('phrases.invitationLinks')}
              </Typography>

              <Typography variant="caption">
                {translate('phrases.deleteInvitationLinksHelper')}
              </Typography>
            </Box>

            {loading ? (
              <CircularProgress />
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: "4px", maxHeight: 500, overflowY: 'auto' }}>
                {links.map((link) => {
                  const isMarkedForDelete = itemsToDelete.includes(link.id!);

                  return (
                    <Stack
                      direction="column"
                      gap={2}
                      key={link.id}
                      sx={{
                        backgroundColor: '#253238',
                        borderRadius: 1,
                      }}
                    >
                      <Stack direction="row" alignItems="start" gap="4px">
                        <IconButton onClick={() => toggleDelete(link.id!)} disabled={deleting} style={{ width: '24px', height: '24px' }}>
                          {isMarkedForDelete ? (
                            <RedoIcon sx={{ transform: 'scale(-1, 1)', color: '#BEC2C3' }} />
                          ) : (
                            <DeleteIcon sx={{ color: '#EB4F4A' }} />
                          )}
                        </IconButton>

                        <Stack direction="column" gap="4px">
                          <Typography
                            sx={{
                              textDecoration: isMarkedForDelete ? 'line-through' : 'none',
                              opacity: isMarkedForDelete ? 0.6 : 1,
                              fontSize: 14,
                            }}
                          >
                            {removeHTTPProtocol(getInvitationLink(link))}
                          </Typography>
                          {isMarkedForDelete ? (
                            <Typography sx={{ color: '#BEC2C3', fontSize: 14 }}>
                              {translate('phrases.isMarkedForDeleteHelper')}
                            </Typography>
                          ) : (
                            <Typography sx={{ color: '#BEC2C3', fontSize: 14 }}>
                              {translate('phrases.linkFor')} {link.abledRoles.map((item) => lowercaseRoleFirst(item.name)).join(', ')}
                            </Typography>
                          )}
                        </Stack>
                      </Stack>
                    </Stack>
                  );
                })}
              </Box>
            )}

            {deleting && <CircularProgress size={24} sx={{ color: 'white' }} />}
          </Box>
        </Box>
      </Modal>
    </>
  );
});
